import { isPrime } from './primes';

export interface PrimeFactor {
  prime: number;
  exponent: number;
}

export function factor(n: number): PrimeFactor[] {
  if (!Number.isInteger(n) || n < 1) return [];
  if (n === 1) return [];
  const out: PrimeFactor[] = [];
  let value = n;
  let p = 2;
  while (p * p <= value) {
    if (value % p === 0) {
      let exponent = 0;
      while (value % p === 0) {
        value = value / p;
        exponent += 1;
      }
      out.push({ prime: p, exponent });
    }
    p = p === 2 ? 3 : p + 2;
  }
  if (value > 1) out.push({ prime: value, exponent: 1 });
  return out;
}

export function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x;
}

export function lcm(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return Math.abs((a / gcd(a, b)) * b);
}

export function divisors(n: number): number[] {
  if (!Number.isInteger(n) || n < 1) return [];
  const small: number[] = [];
  const large: number[] = [];
  for (let i = 1; i * i <= n; i += 1) {
    if (n % i === 0) {
      small.push(i);
      if (i !== n / i) large.unshift(n / i);
    }
  }
  return small.concat(large);
}

export function sumProperDivisors(n: number): number {
  return divisors(n)
    .filter((d) => d !== n)
    .reduce((s, d) => s + d, 0);
}

export type NumberClass = 'perfect' | 'abundant' | 'deficient';

export function classifyNumber(n: number): NumberClass {
  const s = sumProperDivisors(n);
  if (s === n) return 'perfect';
  if (s > n) return 'abundant';
  return 'deficient';
}

function digitSum(n: number): number {
  let total = 0;
  let value = Math.abs(n);
  while (value > 0) {
    total += value % 10;
    value = Math.floor(value / 10);
  }
  return total;
}

export function isSmithNumber(n: number): boolean {
  if (n < 2 || isPrime(n)) return false;
  const factorisation = factor(n);
  const factorDigitSum = factorisation.reduce(
    (total, { prime, exponent }) => total + digitSum(prime) * exponent,
    0,
  );
  return digitSum(n) === factorDigitSum;
}

export function isCarmichaelNumber(n: number): boolean {
  if (n < 2 || isPrime(n)) return false;
  if (n % 2 === 0) return false;
  const factorisation = factor(n);
  if (factorisation.length < 3) return false;
  for (const { exponent } of factorisation) {
    if (exponent !== 1) return false;
  }
  for (const { prime } of factorisation) {
    if ((n - 1) % (prime - 1) !== 0) return false;
  }
  return true;
}
